export type ApiEndpointMap = ReturnType<typeof createApiEndpointTemplate>;

export const createApiEndpointTemplate = (env: any) => ({
    login: `${ env.authBaseUrl }/api/login`,
    refreshToken: `${ env.authBaseUrl }/api/refresh/validate`,
    resetPassword: `/employees/:id/reset-password`,
    passwordReset: `/users/reset-password`,
    reCaptchaSiteVerify: `/recaptcha/verify`,

    // user
    getUser: `/user`,
    getRMAUser: `RMAclone/:encryptToken`,
    saveUser: `/user`,
    saveUserTerms: `/user/terms/:ipAddress`,
    getCustomer: `/customer`,
    getCustomerWebHooks: `/customers/webhooks`,
    getCustomerBillToAccounts: `/customers/bill-to-accounts`,
    getCustomerImages: `/customers/retrieve-images`,
    getUsers: `/customer-users`,
    addUser: `/users/add`,
    disableUser: `/user/delete`, // ?userId={userId}
    updateUser: `/user/edit`, // ?userId={userId}
    signupUser: `/customer/signup`,
    updateDefaultTabIndex: `/user/update_defaultTabIndex`,

    // contact us
    contactUsUK: `/contact-us/gb`,
    contactUsGB: `/contact-us/gb`,
    contactUsDE: `/contact-us/de`,
    contactUs_US: `/contactus/us`,
    contactUs_UK: `/contactus/gb`,
    contactUs_GB: `/contactus/gb`,
    contactUs_DE: `/contactus/de`,
    contactUs_HK: `/contactus/hk`,
    contactUs_TH: `/contactus/th`,
    contactUs_JP: `/contactus/jp`,
    contactUs_IT: `/contactus/it`,
    contactUs_FR: `/contactus/fr`,

    //claims
    // submitClaims: `/claims/submit`,
    hasClaims: `/claims/has-claims`,
    getClaims: `/claims`,
    getClaim: `/claims/:id`,
    findClaims: `/claims`,
    downloadClaims: `/claims/export`,
    createClaim: `/claims/submit`,
    updateClaim: `/claims`,
    deleteClaim: `/claims`,
    withdrawClaim: `/claims/:id/withdraw`,
    findClaimShipments: `/claims/shipments`,
    getClaimDraft: `/claims/details`, // ?claimNumber=xxx&trackingNumber=yyy
    getClaimDetails: `/claims/details`, // ?claimId=xxx
    saveClaimDraft: `/claims/draft`,
    deleteClaimDraft: `/claims/draft`,
    getClaimDocuments: `/claims/documents`,
    uploadClaimDocuments: `/claims/documents`,
    deleteClaimDocument: `/claims/document`, // DELETE ?claimId=xxx&documentId=xxx
    deleteClaimDocuments: `/claims/documents`, // DELETE ?claimId=xxx&documentId=xxx,yyy,zzz
    getClaimCommodityTypes: `/claims/product-categories`,
    getClaimStatusSummary: `/claims/statuses/summary`,
    getClaimStatuses: `/claims/statuses`, // ?claimId=123,456&force=true
    claims: {
        conversations: {
            metadata: `/claims/:claimId/conversations/metadata`,
            get: `/claims/:claimId/conversations`, // ?topic=general
            sync: `/claims/:claimId/conversations/sync`, // ?topic=general
            createMessage: `/claims/:claimId/conversations/messages`,
            updateMessage: `/claims/:claimId/conversations/messages`, // ?messageId=cc:163009
        },
        documents: {
            get: `/claims/:claimId/documents`,
            uploadSingle: `/claims/document`,
            uploadMultiple: `/claims/documents`,
            deleteSingle: `/claims/document`,
            deleteMultiple: `/claims/documents`,
        },
    },

    // company profile
    enrollPaperlessCommercialInvoice: `/customers/terms-paperless`, // POST
    validatePaperlessInvoiceCapability: `/validate/ups-paperless`, // GET
    uploadCustomerDocuments: `/customer-upload/document`, // POST
    deleteCustomerDocument: `/customers/remove-images/:id`, // DELETE
    deleteCustomerDocuments: `/customers/remove-images`, // DELETE
    updateCustomerCommercialInvoiceType: `/customers/commercialinvoicetype/:id`,

    // reset password
    changePassword: `/users/password/reset`, // ?userId={userId}
    validateToken: `/validate-token/:token`,
    changePasswordByToken: `/change-password/:token`,

    // terms and conditions
    getTerms: `/terms`,
    getTerm: `/terms/:id`,
    getTermsSigned: `/terms/signed`,
    getTermsRma: `/terms/rma/:customerId`,
    saveTerms: `/terms`,
    saveTermsTemp: `/terms/tempuser`,
    customerProductTermsConditions: `/product-terms`, // for all HTTP methods

    // dashboard
    getAnnouncements: `/marketing/announcements`, // ?countryCode=all
    getEvents: `/marketing/events`, // ?countryCode=all
    getNews: `/marketing/news`, // ?countryCode=all
    getDashboardBanners: `/marketing/banners`, // ?stationID={stationID}&device={device}
    getSiteAnnouncements: `/marketing/announcements/web/:shippingStationId`,
    getStatusCount: `/shipments/delivery-defense/status-count`,
    ddFeedback: `/customers/feedback`,

    // contacts
    getApContact: `/contacts/ap`, // ?Except1row={Except1row}
    getCorporateContact: `/contacts/corporate`,
    getMailingContact: `/contacts/mailing`,
    saveApContact: `/contacts/insertap`,
    deleteApContact: `/contacts/delete/ap/:contactId`,
    updateApContact: `/contacts/update/ap`, // ?contactId={contactId}
    updateCorporateContact: `/contacts/update/corporate`, // ?contactId={contactId}
    updateMailingContact: `/contacts/update/mailing`, // ?contactId={contactId}

    // address book
    getAddressBookUri: `/address-book`, // ?contactid={contactid}
    getContactAddress: `/address-book/:contactId`,
    getAddressBookPage: `/address-book`, // ?page={page}&per_page={per_page}
    getCarrierAddressBook: `/address-book/express/ship-to`, // ?carriercode={carriercode}
    // searchAddressBook: `${env.v3BaseApiUrl}/address-book/search`, // ?carriercode={carriercode}&query={query}&column={column}
    searchCarrierAddressBook: `/address-book-search`, // ?carriercode={carriercode}&query={query}&column={column}
    saveAddressBook: `/address-book`,
    deleteAddressBook: `/address-book/:contactId`,
    shareAddressBook: `/address-book/share/:contactId/is-shared/:isShared`,
    expressAddressBook: `/address-book/express/:contactId/is-express/:isExpress`,
    uploadAddressBook: `/address-book/import`,

    // locations
    getLocations: `/locations`,
    getLocation: `/locations/:locationId`,
    checkDefaultLocation: `/locations/check-default/:locationId`,
    checkGuestLocation: `/locations/check-guest/:locationId`,
    deleteLocation: `/locations/:locationId`,
    saveLocation: `/locations`,
    saveDefaultLocation: `/locations/default/:contactId`,
    getAccessPoints: `/locations/accesspoints/:carrierCode`,
    getPickupLocations: `/locations/pickup/:carrierCode`,
    getUsedPickupLocations: `/delivery-defense/previous-pickup-locations/:carrierCode`,

    // countries
    getCountry: `/countries/:countryCode`,
    getCountryRma: `/countries/rma`,
    getCarrierCountries: `/countries`, // ?carriercode={carriercode}&isEU={isEU}&selectiontype={selectiontype}

    // claims
    getClaimContact: `/claims-contact`,
    saveClaimPackageInvestigation: `/claims-package-investigation`,

    // shipments
    getShipment: `/shipments/:shipmentId`,
    getShipmentByTrackingId: `/shipments/tracking/:trackingId`,
    getShipmentPage: `/shipments`, // ?page={page}&per_page={per_page}
    getHighValueShipments: `/shipments/high-value`,
    getRecentShipments: `/shipments/recent`,
    getUserLatestShipments: `/shipments/top-5-shipment-by-user`,
    searchShipment: `/shipments/search`, // ?querystring={querystring}
    searchShipments: `/shipments/search`, // ?q={keyword}
    searchReportedShipments: `/reported-shipments/search`, // ?q={keyword}
    deleteShipment: `/shipments/:shipmentId`,
    saveWorldShipShipments: `/shipments/worldship`,
    saveShipments: `/shipments`,
    confirmShipment: `/shipments/:quoteId`,
    checkDoNotShip: `/shipments/check-do-not-ship`, // ?zip={zip}&carrierCode={carrierCode}&countryCode={countryCode}
    checkRestricted: `/address/check-restricted`, // ?zip={zip}&insuredvalue={insuredvalue}&carrier={carrier}&servicecode={servicecode}
    checkSchedulePickup: `/Users/pickup/today`, // ?shipfromid={shipfromid}&scheduleddate={scheduleddate}&carriercode={carriercode}&serviceCode={serviceCode}
    getFedExLocations: `/shipments/fedex-locator`, // ?zip={zip}&phonenumber={phonenumber}
    getPowerOfAttorneyContent: `/shipments-power-of-attorney-content`,
    createHighValueShipment: `/HighValue`, // ?quoteid={quoteid}
    getShipmentLabel: `/shipments/label`, // ?shipmentId={shipmentId}
    getCommercialInvoice: `/shipments/commercial-invoice/:shipmentId`,
    getHandheldSignature: `/tracking/handheld-signature`, // ?shipmentId={shipmentId}&whatImage={whatImage}
    getReturnServices: `/returnServices`, //?carrierCode={carriercode}

    // quotes
    getQuotes: `/quotes`, // ?page={page}&per_page={per_page}
    getQuote: `/quotes/:quoteId`,
    addQuotes: `/quotes`,
    saveQuote: `/save-quote/:quoteId`,
    updateQuotes: `/quote/update`,
    deleteQuote: `/quotes/:quoteId`,
    submitToACE: `/shipments/ace`,
    getSavedQuotes: `/save-quotes`, // ?page={page}&per_page={per_page}
    requestQuote: `/send-email/price-comparison`, // POST

    // commodities
    getCommodities: `/commodities`,
    getHarmonizedCommodities: `/commodities/country/:countryCode`,
    getCommoditiesByCustomer: `/customer-commodities`,

    // reported shipments
    getReportedShipments: `/reported-shipments`, // ?daysOld={daysOld}&page={page}&per_page={per_page}
    deleteReportedShipment: `/reported-shipments/:shipmentId`,
    reportShipmentUS: `/shipments/report/us`,
    reportShipmentEU: `/shipments/report/eu`,
    bulkReportShipmentEU: `/shipments/reported/eu`,
    validateTrackingNumber: `/validate/shipment/reported/tracking-number-exists/:trackingNumber`,
    validateTrackingNumbers: `/validate/shipment/reported/tracking-numbers-exist`,
    getReportServices: `/report-services`,
    uploadFile: `/shipments/upload-file`,
    updateFile: `/shipments/update-file`,
    getFileUploads: `/shipments/get-file-uploads`,
    getFileContent: `/shipments/get-file-content/:id`,

    // tracking
    getTrackingDetails: `/tracking-results`, // ?shipmentid={shipmentid}&shipmenttype={shipmenttype}

    // invoices
    getBillingHistory: `/invoices`, // ?page={page}&per_page={per_page}&from={from}&to={to}
    getInvoice: `/invoices/:invoiceId`,
    searchInvoice: `/invoices/search`, // ?q`,
    downloadInvoiceExcel: `/invoicedetails/:invoiceId`,
    // scheduled pickup
    getScheduledPickups: `/scheduled-pickups`,
    deleteScheduledPickupQuote: `/scheduled-pickups/:quoteId`,

    // carrier
    getCarrierKey: `/carriers/keys/:carrierCode`,
    getShippingCarrierServices: `/carriers/services`, // ?carrierCode=1&isDomestic=true&shipFromCountryCode=US
    getShippingCarrierPackageTypes: `/carriers/package-types`, // ?carrierservicecode=01&carriercode=01
    getPackageWeight: `/carriers/weight`, // ?length=1&width=2&height=3
    getServicesByCountries: `/carriers/services-by-country`, // ?shipToCountry={CountryCode}&ShipToZip={Zip}&ShipFromCountry={CountryCode}&ShipFromZip={Zip}`

    // lookup
    getCountries: `/lookup/countries`,
    getCarrierServices: `/lookup/carrier-services/:carrierCode`,
    getCustomerCarriers: `/lookup/carriers/customer/:customerId`,

    // address
    getAddressByZipCode: `/validate/postal-code/:postalCode`,
    validateAddressUPS: `/validate/address/UPS`,
    validateAddressFedex: `/validate/address/Fedex`,
    validateAddress: `/validate/address`,
    validateShipToZipRestriction: `/validate/ship-to-zip/is-restricted`,

    // payments
    getPaymentPage: `/payments`, // ?page={page}&per_page={per_page}

    // payment receipt
    getPaymentReceipt: `/payment/receipt/:paymentId`,

    // credit cards
    getCreditCards: `/credit-cards`,
    chargeCreditCard: `/credit-cards`,
    changePrimaryCreditCard: `/credit-cards/primary`,
    removeCreditCard: `/credit-cards/:usercreditcardid`,
    createAnetUser: `/credit-cards/AnetProfile`,
    getCreditCardForms: `/credit-cards/CCForm`,
    addPaymentProfile: `/credit-cards/AddPaymentProfile`,
    editPaymentProfile: `/credit-cards/EditPaymentProfile`,
    getCreditCardTerms: `/Terms/credit-card`,
    acceptCreditCardTerms: `/Terms/credit-card`,
    getPrimaryCreditCard: `/credit-cards/primary`,

    // credit history
    getCreditHistory: `/credits`, // ?page={page}&per_page={per_page}&from={from}&to={to}

    // bir
    getShipmentExceptions: `/shipments/exceptions`,
    updateShipmentDate: `/shipment-date`,
    voidShipment: `/shipments/:shipmentId`,

    // cost estimator
    getRates: `/estimator`,
    getCustomerServices: `/customer-services`, //?countryCode=US,

    // upload files
    uploadFiles: `/files/customer-assets`,

    // drop shipping label
    dropShipmentLabel: `/shipments/drop`,

    // reports
    getEndOfDayReport: `/shipments/reports/end-of-day`, // ?reportdate={2016-02-15}
    getEndOfDayReportByLocation: `/shipments/reports/end-of-day-by-location`, // ?reportdate={2016-02-15}&carrier={fedex}&locationid={4B7C4C97-310F-4ED2-8D4E-0F908A10C680}
    getEndOfDayReportByUser: `/shipments/reports/end-of-day-by-user`, // ?reportdate={2016-02-15}&carrier={fedex}&userId={10618}
    getEndOfDayReportLocations: `/customers/end-of-day-report-locations`,
    getEndOfDayReportUsers: `/customers/end-of-day-report-users`,
    getEndOfDayHighValueReport: `/shipments/reports/end-of-day-high-value`,

    // rewardship
    getRewardsHistory: `/customers/rewards-history`, // ?page={page}&per_page={per_page}
    redeemRewards: `/customers/redeem-rewards`,
    getRewardsHistoryHeader: `/customers/rewards-history-header`,

    // digital rewardship
    getDWRewardsHistory: `/invoices/dw-invoices-history`,
    getAutoRedeemFlag: `${ env.digitalWalletBaseApiUrl }/autoredeem`,
    postAutoRedeemFlag: `${ env.digitalWalletBaseApiUrl }/autoredeem`,
    getDWBalance: `${ env.digitalWalletBaseApiUrl }/balance/:customerId`,
    getCheckDWCustomer: `/customers/check-dwcustomer`,
    postRedeem: `${ env.digitalWalletBaseApiUrl }/redeem`,  // Post Redemption API
    postTCFlag: `/customers/update-tcFlag`,
    postTCFlagForOthers: `/customers/update-tcFlagForOthers`,
    getTermsAndCondAccepted: `/customers/tcFlag`,
    getTermsAndCondAcceptedForOthers: `/customers/tcFlagForOthers`,
    getDWOpenInvoices: `/invoices/dw-open-invoices`,
    getKPIMetrics: `${ env.digitalWalletBaseApiUrl }/dwkpimetrics`,
    postAdvancedFilter: `/invoices/dw-advanced-invoicehistory`,

    // notify templates
    getNotifyTemplates: `/notify/templates`,
    getNotifyTemplate: `/notify/templates/:templateId`,
    addNotifyTemplate: `/marketing/save-notifyemailtemplate`,
    setPrimaryNotifyTemplate: `/shipments/manage-email-templates/default/:templateId`,
    deleteNotifyTemplate: `/shipments/manage-email-templates/:templateId`,

    // supply-order
    sendSupplyOrder: `/supplies`,
    getSupplyOrderHistory: `/supplies/order-history`, // ?page={page}&per_page={per_page}
    getSupplyOrderDetails: `/supplies/:RequestId`,

    // e-invoice
    getContactCorporate: `/contacts/corporate`,
    updateEInvoiceSubscription: `/customers/setting/e-invoice`, // payload: EInvoiceSubscription

    // send email
    // TODO: correct these endpoints!
    sendEmail: `http://169.46.65.71/node/parcelpro-server/sendemail`,
    sendClaimEmail: `/claim-email`,
    sendHighInsuredValueEmail: `/high-insured-value-email`,
    // supply-order
    // TODO: correct these endpoints!
    confirmSupplyOrder: `/confirm-supply-order`,

    // configuration
    getUserCarrierConfiguration: `/shipments/configuration/:carrierCode`,

    // upload
    uploadPhoto: `/Upload/uploadPhoto`, // ?fileName={fileName}&description={description}

    // mobile view
    getMobileToken: `/mobile-ship-token/:userId`,
    tokenLogin: `${ env.authBaseUrl }/api/intratoship-jwt/:loginToken`,

    // s3
    downloadFileFromS3: `/files/s3/:bucketName/:fileName/`,
    downloadPDFFileFromS3: `/files/s3/members-pdf.upsc/:fileName`,

    // Single Sign On
    generateSingleSignOn: `${ env.authBaseUrl }/api/SSO/Validate`,

    // request HV increase - POST
    requestHighValueIncrease: `/email-request-HVIncrease`,

    // DeliveryDefense
    getRemainingDDCredits: `/customers/remainingDDScores`,
    getAgreementBeta: `/termsconditions/:productId`,
    acceptDDAgreementBeta: `/customers/DeliveryDefense/Terms`,
    declineDDAgreementBeta: `/customers/DeliveryDefense/Terms`,
    turnOffDeliveryDefense: `/customers/DeliveryDefense/Terms`,
    subscribeToDeliveryDefense: `/customers/deliverydefense/subscription`,
    getDDCustomerSubscription: `/customers/subscription`,
    getDDSubscriptionType: `/deliverydefense/subscription-type/:subscriptionType`,
    requestMoreScores: `/customers/deliverydefense/needscores`,
    getCustomerPendingAgreementBetaFeatures: `/customers/features/beta/pending`,
    subscriptionDetails: `/customers/subscription`,
    scoreHistory: `/deliverydefense/address`,
    getAddressUpload: `/deliverydefense/address-upload/:fileUploadId`,
    getRecentUploads: `/deliverydefense/recent-uploads`,
    addressUpload: `/deliverydefense/address-upload`,
    addressReview: `/deliverydefense/address-review`,
    addressUploadDraft: `/deliverydefense/address-upload/draft`,
    downloadAddressUpload: `/deliverydefense/address-upload/file`,

    // Change Password
    validatePassword: `${ env.authBaseUrl }/AuthHash/Check`,
    updatePassword: `${ env.authBaseUrl }/AuthHash/Update`,
    secureUpdatePassword: `${ env.authBaseUrl }/AuthHash/SecureUpdate`,

    // Track Shipments
    getCustomerTrackingScans: `/tracking`,
    getTrackingStatusCounts: `/tracking/statuses/count`,
    getTrackingScans: `/tracking/:trackingNumber`,

    // Sales Persons
    getSalesPersons: `/sales/salesperson/search`,

    // Access Point Code
    resendAccessPointCode: `/shipments/:shipmentId/access-point-code/resend`,

    // MFA
    requestMFA: `/shipments/:shipmentId/access-point-code/mfa`,
    validateMFA: `/shipments/:shipmentId/access-point-code/mfa/validate`,

    // Orders
    getECommercePlatforms: `/e-commerce/platforms`,
    getOrderCategories: `/orders/categories`,
    getOrderStatuses: `/orders/statuses`,
    getOrderActions: `/orders/actions`,
    getOrderCategoryStatuses: `/orders/categories/:categoryId/statuses`,
    getOrderCategoryStatusActions: `/orders/categories/:categoryId/statuses/:statusId/actions`,
    getAllOrderCategoryStatusActions: `/orders/category-status-actions`,
    getOrderCarriers: `/orders/carriers`,
    searchOrders: `/orders`,

    // Billing
    getLatestUserBillingContact: `/billing/contact`,
}) as const;
